/**
 * ============================================================================
 * ⭐⭐ THE MATCH SCREEN'S SIDE-BY-SIDE (2026-09-28)
 * ============================================================================
 *
 * > Pete: *"This screen should show you details of the appintment on one side (6
 * > criteria) and details of the WMS order on the other side (6 criteria). Along
 * > with the obvious client, carrier. So the scheduler can say, yes this order goes
 * > with this appointment."*
 *
 * Popup answers (Decision 241): the six rows are **Direction, Date, PO, Reference,
 * Container, BOL / PRO**, under Client and Carrier; a list on the left and this
 * comparison on the right, attaching only on an explicit button; a **tick** where
 * the two sides agree, **amber** where both have a value and they differ, **grey**
 * where one side is blank.
 *
 * ⭐ A PURE FUNCTION, so a guard can run it and so the rows are one list rather than
 * eight conditions spread through JSX.
 *
 * ⛔ IT ADVISES; IT NEVER DECIDES. The scheduler presses Attach. A tick means "these
 * two strings agree once case, spaces and dashes are ignored" — not "this is the
 * order". Nothing here attaches, filters or reorders anything.
 *   ** A COMPARISON THAT CHOOSES FOR YOU IS A MATCHER WITH NO AUDIT. **
 *
 * ⚠️ AN ORDER HAS NO CARRIER FIELD. Its WMS **Ship Via** is the nearest thing and is
 * labelled as such on screen; a Ship Via of "FEDEX GROUND" against a carrier code
 * "FXFE" is amber and that is honest — they are not the same string. The scheduler
 * reads it.
 */

export type Verdict = 'match' | 'differ' | 'blank';

export interface CompareRow {
    key: 'client' | 'carrier' | 'direction' | 'date' | 'po' | 'reference' | 'container' | 'bolPro';
    label: string;
    appt: string | null;
    order: string | null;
    verdict: Verdict;
}

/** The appointment fields the comparison reads. */
export interface CompareAppt {
    direction: string;
    scheduledStartUtc: string;
    clientCode: string;
    clientName: string | null;
    carrierCode: string;
    carrierName: string | null;
    poNumber: string | null;
    referenceNumber: string | null;
    containerNumber: string | null;
    bolNumber?: string | null;
    documentType?: string | null;
    documentNumber?: string | null;
}

/** The WMS order fields the comparison reads (`OrdersPanelRowDto`). */
export interface CompareOrder {
    direction: 'INBOUND' | 'OUTBOUND' | null;
    effectiveDate: string | null;
    customer: string | null;
    businessUnit: string | null;
    shipVia?: string | null;
    customerOrderNumber: string | null;
    customerReference: string | null;
    containerNumber: string | null;
    bolNumber: string | null;
    proNumber: string | null;
}

/** Case, spaces, dashes, underscores, dots and slashes ignored — how numbers are read at a gate. */
export function normId(s: string | null | undefined): string {
    return (s ?? '').toLowerCase().replace(/[\s\-_./]+/g, '');
}

const clean = (s: string | null | undefined): string | null => {
    const t = (s ?? '').trim();
    return t === '' ? null : t;
};

/** Any value on the left equal to any value on the right (normalised). */
function anyEqual(left: (string | null | undefined)[], right: (string | null | undefined)[]): boolean {
    const L = left.map(normId).filter(Boolean);
    const R = right.map(normId).filter(Boolean);
    return L.some(l => R.includes(l));
}

/** Loose, for names: one contains the other, both at least 3 characters. */
function anyContains(left: (string | null | undefined)[], right: (string | null | undefined)[]): boolean {
    const L = left.map(normId).filter(x => x.length >= 3);
    const R = right.map(normId).filter(x => x.length >= 3);
    return L.some(l => R.some(r => l.includes(r) || r.includes(l)));
}

function verdictOf(appt: string | null, order: string | null, agrees: boolean): Verdict {
    if (appt === null || order === null) return 'blank';
    return agrees ? 'match' : 'differ';
}

/** The appointment's LOCAL date, as the browser shows its time (see `hhmm`). */
export function apptLocalDate(iso: string): string | null {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const dirWord = (d: string | null | undefined): string | null =>
    d === 'INBOUND' ? 'Inbound' : d === 'OUTBOUND' ? 'Outbound' : null;

export function compareApptToOrder(a: CompareAppt, o: CompareOrder): CompareRow[] {
    const rows: CompareRow[] = [];
    const push = (key: CompareRow['key'], label: string, appt: string | null, order: string | null, agrees: boolean) =>
        rows.push({ key, label, appt, order, verdict: verdictOf(appt, order, agrees) });

    // ---- the obvious two, first
    const apptClient = clean([a.clientName, a.clientCode].filter(Boolean).join(' · '));
    const orderClient = clean([o.customer, o.businessUnit].filter(Boolean).join(' · '));
    push('client', 'Client', apptClient, orderClient,
        anyEqual([a.clientCode, a.clientName], [o.customer, o.businessUnit])
        || anyContains([a.clientName], [o.customer]));

    const apptCarrier = clean([a.carrierName, a.carrierCode].filter(Boolean).join(' · '));
    push('carrier', 'Carrier / Ship Via', apptCarrier, clean(o.shipVia),
        anyEqual([a.carrierCode, a.carrierName], [o.shipVia]) || anyContains([a.carrierName, a.carrierCode], [o.shipVia]));

    // ---- the six
    push('direction', 'Direction', dirWord(a.direction), dirWord(o.direction), a.direction === o.direction);

    /* ⚠️ A day-only appointment still has a start instant on its day, so the date
       is the same either way; only the TIME is meaningless for it. */
    const aDate = apptLocalDate(a.scheduledStartUtc);
    const oDate = clean(o.effectiveDate)?.slice(0, 10) ?? null;
    push('date', 'Date', aDate, oDate, aDate !== null && aDate === oDate);

    push('po', 'PO', clean(a.poNumber), clean(o.customerOrderNumber), anyEqual([a.poNumber], [o.customerOrderNumber]));
    push('reference', 'Reference', clean(a.referenceNumber), clean(o.customerReference),
        anyEqual([a.referenceNumber], [o.customerReference]));
    push('container', 'Container', clean(a.containerNumber), clean(o.containerNumber),
        anyEqual([a.containerNumber], [o.containerNumber]));

    /* ⚠️ The appointment's BOL column first; a document number typed as BOL/PRO is the
       fallback, labelled with its type so it is not mistaken for the column. */
    const apptDoc = clean(a.bolNumber)
        ?? (clean(a.documentNumber) ? `${clean(a.documentType) ?? 'Doc'} ${clean(a.documentNumber)}` : null);
    const orderDoc = clean([o.bolNumber ? `BOL ${o.bolNumber}` : null, o.proNumber ? `PRO ${o.proNumber}` : null]
        .filter(Boolean).join(' · '));
    push('bolPro', 'BOL / PRO', apptDoc, orderDoc,
        anyEqual([a.bolNumber, a.documentNumber], [o.bolNumber, o.proNumber]));

    return rows;
}

/** How many rows agree — the list's "✓ N" chip. */
export function matchCount(rows: readonly CompareRow[]): number {
    return rows.filter(r => r.verdict === 'match').length;
}
